import { Card, CardContent } from "@volca/ui/components/ui/card";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { periodNoun } from "@volca/ui/trades";
import { dayjs } from "@/core/utils/date";
import type { RecordObject, RecordRef } from "@/profitability/records";
import type { RecordList } from "@/profitability/records/record-panel";
import { useReviewSpec } from "@/profitability/review/review-spec";
import type { FindingStatus } from "@/profitability/review/types";
import { formatReportPeriod, runDates } from "@/profitability/utils";
import { OverviewChanges } from "./overview-changes";
import type { RunReview } from "./period-review";
import { PeriodStrip } from "./period-strip";
import { PeriodTimeline } from "./period-timeline";
import { ReviewProgress } from "./review-progress";
import type { ReportOverview } from "./types";

export const PERIOD_DETAILS_COLUMNS =
  "@5xl:grid-cols-[minmax(0,2fr)_minmax(18rem,1fr)]";

/** Group volume with review activity; give changes their own reading width. */
export const OverviewDetails = ({
  overview,
  objects,
  counts,
  findingsError,
  review,
  recordsPending,
  onOpenRecord,
}: {
  overview: ReportOverview;
  objects: RecordObject[];
  counts: Partial<Record<FindingStatus, number>> | undefined;
  findingsError: boolean;
  review: RunReview | null;
  recordsPending: boolean;
  onOpenRecord: (ref: RecordRef, list?: RecordList | null) => void;
}) => {
  const spec = useReviewSpec();
  const { run, running, previous, changes } = overview;
  if (!run) return null;
  const sections = spec.sections;
  const dates = runDates(run);
  const noun = dates
    ? periodNoun(dayjs(dates.end_date).diff(dates.start_date, "day") + 1)
    : "period";
  const work = objects.find((object) => object.dataset.id === spec.work);
  const week =
    sections.includes("week") &&
    !!spec.work &&
    dates &&
    (recordsPending || work?.dataset.period_field);
  const progress =
    !findingsError &&
    sections.includes("progress") &&
    !!spec.work &&
    (!counts || Object.values(counts).some((count) => count > 0));
  const timeline = sections.includes("timeline");
  const activity = progress || timeline;
  const since =
    previous?.comparable && (previous.label ?? formatReportPeriod(previous));
  const showChanges = sections.includes("changes") && since;
  if (!week && !activity && !showChanges) return null;
  return (
    <Stack
      render={<section aria-label="Period details" />}
      gap={5}
      className="border-t border-line pt-6"
    >
      <Text render={<h2 />} variant="title">
        Period details
      </Text>
      {(week || activity) && (
        <Stack
          className={cn("grid min-w-0 items-start gap-6", {
            [PERIOD_DETAILS_COLUMNS]: !!week && activity,
          })}
        >
          {week && (
            <Card size="sm" className="min-w-0">
              <CardContent>
                {recordsPending ? (
                  <Skeleton className="h-48 w-full" />
                ) : (
                  work &&
                  dates && (
                    <PeriodStrip
                      dates={dates}
                      object={work}
                      onOpenRecord={onOpenRecord}
                    />
                  )
                )}
              </CardContent>
            </Card>
          )}
          {activity && (
            <Card size="sm" className="@container/activity min-w-0">
              <CardContent
                className={cn("grid min-w-0 gap-6", {
                  "@2xl/activity:grid-cols-2 @2xl/activity:gap-8":
                    progress && timeline,
                })}
              >
                {progress &&
                  (counts ? (
                    <ReviewProgress counts={counts} />
                  ) : (
                    <Skeleton className="h-24 w-full" />
                  ))}
                {timeline && (
                  <PeriodTimeline
                    run={run}
                    running={running}
                    flagged={
                      work
                        ? {
                            count: overview.flagged ?? 0,
                            noun: work.name.toLowerCase(),
                            plural: work.plural.toLowerCase(),
                          }
                        : null
                    }
                    review={review}
                  />
                )}
              </CardContent>
            </Card>
          )}
        </Stack>
      )}
      {showChanges && (
        <OverviewChanges
          changes={changes}
          objects={objects}
          since={since}
          noun={noun}
          onOpenRecord={onOpenRecord}
        />
      )}
    </Stack>
  );
};
